/**
 * VibeCompliance — homepage product demo.
 *
 * A real, interactive composer + scripted agent run. Not a video.
 * Lowers the "this looks like work" barrier for CISOs / compliance leads
 * by showing cSquare agents handle the regulatory sprawl in 5 seconds.
 *
 * Direction: Composer-forward (TextQL parallel). Fibo sits as a small
 * avatar at the top of the composer chrome — a "signed by Fibo" mark,
 * not a centerpiece.
 *
 * No LLM call, no edge function, no API cost.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  ArrowUp,
  ShieldCheck,
  Plug,
  FileText,
  Workflow,
  Library,
  GitBranch,
  CheckCircle2,
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import LocaleLink from "@/components/LocaleLink";
import Fibo from "@/components/brand/Fibo";
import { Reveal } from "@/components/ui/reveal";
import { cn } from "@/lib/utils";
import { useTypewriter } from "@/hooks/useTypewriter";
import { VIBE_SCENARIOS, type VibePersona, type VibeScenario } from "@/data/vibeScenarios";
import { getBrandGlyph } from "@/data/brandGlyphs";
import FrameworkLogo from "@/components/marketing/FrameworkLogo";
import { getFrameworkLogoDomain } from "@/data/frameworkLogos";


type RunState = "idle" | "running" | "done";





/** Card + chip copy is keyed here and resolved with t() at render time (see
 *  useLocalizedCards below) so the hero re-renders correctly on locale change.
 *  Chip keys map to `vibeHero.chips.<key>`; entries with no matching vibeHero
 *  key (brand/framework proper nouns) fall back to a literal label. */
const INPUT_CARD_DEFS = [
  {
    icon: ShieldCheck,
    cardKey: "frameworks" as const,
    chips: [
      { literal: "SOC 2" },
      { literal: "ISO 27001" },
      { literal: "DORA" },
      { literal: "NIS2" },
      { literal: "EU AI Act" },
    ],
  },
  {
    icon: Plug,
    cardKey: "evidence" as const,
    chips: [
      { literal: "Okta" },
      { literal: "AWS" },
      { literal: "GitHub" },
      { literal: "Jira" },
      { literal: "Drive" },
    ],
  },
  {
    icon: Library,
    cardKey: "knowledge" as const,
    chips: [
      { key: "policies" as const },
      { key: "procedures" as const },
      { key: "sops" as const },
      { key: "standards" as const },
      { key: "auditFindings" as const },
    ],
  },
];

const OUTPUT_CARD_DEFS = [
  {
    icon: Workflow,
    cardKey: "workflows" as const,
    chips: [
      { key: "controls" as const },
      { key: "risks" as const },
      { key: "vendors" as const },
      { key: "incidents" as const },
    ],
    animated: true,
  },
  {
    icon: GitBranch,
    cardKey: "models" as const,
    chips: [
      { key: "bpmnFlows" as const },
      { key: "raci" as const },
      { key: "controlNarratives" as const },
    ],
  },
  {
    icon: FileText,
    cardKey: "deliverables" as const,
    chips: [
      { key: "auditPacks" as const },
      { key: "reports" as const },
      { key: "programs" as const },
      { key: "riskAssessments" as const },
      { key: "policies" as const },
      { key: "dpias" as const },
    ],
  },
];

type ChipDef = { key?: string; literal?: string };

/** Resolves a card/chip definition list into the shape ContextCard/FlowNode
 *  render, translating every key against the active locale via t(). */
const useLocalizedCards = (
  defs: {
    icon: FlowCard["icon"];
    cardKey: "frameworks" | "evidence" | "knowledge" | "workflows" | "models" | "deliverables";
    chips: ChipDef[];
    animated?: boolean;
  }[],
  t: (key: string) => string,
) =>
  useMemo(
    () =>
      defs.map((d) => ({
        icon: d.icon,
        label: t(`vibeHero.cards.${d.cardKey}.label`),
        short: t(`vibeHero.cards.${d.cardKey}.short`),
        sub: t(`vibeHero.cards.${d.cardKey}.sub`),
        chips: d.chips.map((c) => (c.key ? t(`vibeHero.chips.${c.key}`) : (c.literal as string))),
        animated: d.animated,
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [defs, t],
  );


/**
 * Floor for the hero diagram's fit-to-fold scale. Below this the card labels
 * stop being readable, so a hero that still cannot fit keeps the strip just
 * under the fold rather than shrinking into illegibility.
 */
const MIN_HERO_FIT = 0.74;

/**
 * Slack left under the strip. The hero keeps settling by a pixel or two after
 * the fit lands (the caption rebalances, the animation advances a frame), and
 * without a guard a two-pixel drift is enough to put the strip's bottom border
 * under the fold, which is exactly the failure this is here to prevent.
 */
const HERO_FOLD_GUARD = 6;

const VibeCompliance = () => {
  const { t, i18n } = useTranslation();

  // Seed the loop at a random scenario so different visitors see different
  // domains first — the full cycle is too long to watch end to end.
  // `?vibe=<scenario-id>` pins a scenario, which keeps visual regression
  // snapshots deterministic in CI.
  const [persona, setPersona] = useState<VibePersona>(() => {
    if (typeof window !== "undefined") {
      const pinned = new URLSearchParams(window.location.search).get("vibe");
      const match = VIBE_SCENARIOS.find((s) => s.id === pinned);
      if (match) return match.id;
    }
    return VIBE_SCENARIOS[Math.floor(Math.random() * VIBE_SCENARIOS.length)].id;
  });

  // Scenario copy (prompt + steps) lives behind i18n keys in vibeScenarios.ts;
  // resolve it here, per active locale, so switching locales updates the
  // running/looping demo instead of freezing it at the English strings.
  const translatedScenarios = useMemo<VibeScenario[]>(
    () =>
      VIBE_SCENARIOS.map((s) => ({
        ...s,
        prompt: t(`vibeHero.scenarios.${s.i18nKey}.prompt`),
        steps: t(`vibeHero.scenarios.${s.i18nKey}.steps`, {
          returnObjects: true,
        }) as string[],
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, i18n.language],
  );

  const scenario = useMemo<VibeScenario>(
    () =>
      translatedScenarios.find((s) => s.id === persona) ?? translatedScenarios[0],
    [persona, translatedScenarios],
  );

  // Invisible sizer for the composer: recomputed from the ACTIVE locale's
  // translated prompts so the reserved height matches the longest string in
  // whichever language is showing, not a hardcoded English constant.
  const longestPrompt = useMemo(
    () =>
      translatedScenarios.reduce(
        (longest, s) => (s.prompt.length > longest.length ? s.prompt : longest),
        "",
      ),
    [translatedScenarios],
  );

  const inputCards = useLocalizedCards(INPUT_CARD_DEFS, t);
  const outputCards = useLocalizedCards(OUTPUT_CARD_DEFS, t);

  const [runState, setRunState] = useState<RunState>("idle");
  const [visibleRuns, setVisibleRuns] = useState(0);
  const [activeInputs, setActiveInputs] = useState(0);
  const [activeOutputs, setActiveOutputs] = useState(0);
  /**
   * The sequence lights every card as it passes and used to leave them lit, so
   * the hero came to rest as a wall of Deep Teal borders. Production settles
   * back to the quiet border and that is the treatment we want: the accent is
   * for the thing that is happening, not for everything that has happened.
   * Cards keep full opacity once settled — this is "finished", not "inactive".
   */
  const [settled, setSettled] = useState(false);
  const timeoutsRef = useRef<number[]>([]);

  // The demo is a looping animation: it only plays while the section is in
  // view and the tab is visible. Nothing here is interactive.
  const sectionRef = useRef<HTMLElement | null>(null);
  const [inView, setInView] = useState(false);
  const [tabVisible, setTabVisible] = useState(true);
  const reduceMotion =
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const playing = inView && tabVisible;

  useEffect(() => {
    if (!sectionRef.current) return;
    const el = sectionRef.current;
    const io = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { threshold: 0.2 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    const onVis = () => setTabVisible(!document.hidden);
    onVis();
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  const { value: typedPrompt, isTyping } = useTypewriter(scenario.prompt, {
    enabled: playing,
    resetKey: persona,
  });

  // Reset the run whenever the scenario rotates
  useEffect(() => {
    setRunState("idle");
    setVisibleRuns(0);
    setActiveInputs(0);
    setActiveOutputs(0);
    setSettled(false);
    timeoutsRef.current.forEach((id) => window.clearTimeout(id));
    timeoutsRef.current = [];
  }, [scenario]);

  useEffect(() => {
    return () => {
      timeoutsRef.current.forEach((id) => window.clearTimeout(id));
    };
  }, []);

  const prompt = typedPrompt;

  const stepCount = scenario.steps.length;

  const startRun = useCallback(() => {
    timeoutsRef.current.forEach((id) => window.clearTimeout(id));
    timeoutsRef.current = [];
    setVisibleRuns(0);
    setActiveInputs(0);
    setActiveOutputs(0);
    setSettled(false);
    setRunState("running");

    if (reduceMotion) {
      setActiveInputs(INPUT_CARD_DEFS.length);
      setActiveOutputs(OUTPUT_CARD_DEFS.length);
      setSettled(true);
      setVisibleRuns(stepCount);
      setRunState("done");
      return;
    }

    // Phase 1 — inputs light up one by one
    INPUT_CARD_DEFS.forEach((_, idx) => {
      const id = window.setTimeout(() => setActiveInputs(idx + 1), 220 + idx * 300);
      timeoutsRef.current.push(id);
    });
    const inputsDone = 220 + INPUT_CARD_DEFS.length * 300;

    // Phase 2 — condensed agent steps
    scenario.steps.forEach((_, idx) => {
      const id = window.setTimeout(
        () => {
          setVisibleRuns(idx + 1);
          if (idx === stepCount - 1) setRunState("done");
        },
        inputsDone + (idx + 1) * 520,
      );
      timeoutsRef.current.push(id);
    });

    // Phase 3 — outputs build in sequence, overlapping the tail of the run
    OUTPUT_CARD_DEFS.forEach((_, idx) => {
      const id = window.setTimeout(
        () => setActiveOutputs(idx + 1),
        inputsDone + 320 + idx * 380,
      );
      timeoutsRef.current.push(id);
    });

    // Phase 4 — settle. A beat after the last output lands, the accent drains
    // out of the cards and the diagram goes quiet again.
    timeoutsRef.current.push(
      window.setTimeout(
        () => setSettled(true),
        inputsDone + 320 + OUTPUT_CARD_DEFS.length * 380 + 900,
      ),
    );
  }, [reduceMotion, scenario, stepCount]);

  // Autoplay: once typing lands, kick off the scripted run.
  useEffect(() => {
    if (!playing || isTyping || runState !== "idle") return;
    if (!prompt) return;
    const id = window.setTimeout(startRun, 400);
    return () => window.clearTimeout(id);
  }, [playing, isTyping, runState, prompt, startRun]);

  // Autoplay: hold the finished state, then rotate to the next scenario.
  useEffect(() => {
    if (runState !== "done" || !playing || reduceMotion) return;
    const id = window.setTimeout(() => {
      setPersona((current) => {
        const idx = VIBE_SCENARIOS.findIndex((s) => s.id === current);
        return VIBE_SCENARIOS[(idx + 1) % VIBE_SCENARIOS.length].id;
      });
    }, 2200);
    return () => window.clearTimeout(id);
  }, [runState, playing, reduceMotion]);


  /* ----- desktop layout measurement: composer alignment + connector paths ----- */
  const workspaceRef = useRef<HTMLDivElement | null>(null);
  const composerBoxRef = useRef<HTMLDivElement | null>(null);
  const inputRefs = useRef<(HTMLDivElement | null)[]>([]);
  const outputRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [composerOffset, setComposerOffset] = useState(0);
  const [overlay, setOverlay] = useState<{
    w: number;
    h: number;
    inputs: string[];
    outputs: string[];
  }>({ w: 0, h: 0, inputs: [], outputs: [] });

  /* ----- mobile layout measurement: vertical spine connectors ----- */
  const mFlowRef = useRef<HTMLDivElement | null>(null);
  const mComposerRef = useRef<HTMLDivElement | null>(null);
  const mInputRefs = useRef<(HTMLDivElement | null)[]>([]);
  const mOutputRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [mOverlay, setMOverlay] = useState<{
    w: number;
    h: number;
    inputs: string[];
    outputs: string[];
  }>({ w: 0, h: 0, inputs: [], outputs: [] });

  useEffect(() => {
    const measure = () => {
      const wrap = mFlowRef.current;
      const composer = mComposerRef.current;
      if (!wrap || !composer) return;
      if (window.innerWidth >= 1024) {
        setMOverlay({ w: 0, h: 0, inputs: [], outputs: [] });
        return;
      }

      const base = wrap.getBoundingClientRect();
      if (!base.width || !base.height) return;
      const c = composer.getBoundingClientRect();
      const cx = c.left + c.width / 2 - base.left;
      const cTop = c.top - base.top;
      const cBottom = c.bottom - base.top;

      const build = (
        refs: (HTMLDivElement | null)[],
        direction: "in" | "out",
      ) =>
        refs
          .filter(Boolean)
          .map((el) => {
            const r = (el as HTMLDivElement).getBoundingClientRect();
            const x = r.left + r.width / 2 - base.left;
            if (direction === "in") {
              const y0 = r.bottom - base.top;
              const mid = (y0 + cTop) / 2;
              return `M${x} ${y0} C ${x} ${mid}, ${cx} ${mid}, ${cx} ${cTop}`;
            }
            const y1 = r.top - base.top;
            const mid = (cBottom + y1) / 2;
            return `M${cx} ${cBottom} C ${cx} ${mid}, ${x} ${mid}, ${x} ${y1}`;
          });

      setMOverlay({
        w: base.width,
        h: base.height,
        inputs: build(mInputRefs.current, "in"),
        outputs: build(mOutputRefs.current, "out"),
      });
    };

    measure();
    const raf = window.requestAnimationFrame(measure);
    const wrap = mFlowRef.current;
    const ro = new ResizeObserver(() => measure());
    if (wrap) ro.observe(wrap);
    window.addEventListener("resize", measure);
    return () => {
      window.cancelAnimationFrame(raf);
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [runState, visibleRuns, activeInputs, activeOutputs, scenario]);

  useEffect(() => {
    const measure = () => {
      const ws = workspaceRef.current;
      const composer = composerBoxRef.current;
      if (!ws || !composer) return;
      // Desktop only — the stacked layout draws no connectors.
      if (window.innerWidth < 1024) {
        setComposerOffset(0);
        setOverlay({ w: 0, h: 0, inputs: [], outputs: [] });
        return;
      }

      const base = ws.getBoundingClientRect();
      const cRect = composer.getBoundingClientRect();

      // 1. Align the composer's vertical center with the middle boxes on both
      //    sides (Evidence sources / Process models). The stacks differ in
      //    height, so target the midpoint between the two middle cards.
      const midInput = inputRefs.current[Math.floor(INPUT_CARD_DEFS.length / 2)];
      const midOutput = outputRefs.current[Math.floor(OUTPUT_CARD_DEFS.length / 2)];
      const centers = [midInput, midOutput]
        .filter(Boolean)
        .map((el) => {
          const r = (el as HTMLDivElement).getBoundingClientRect();
          return r.top + r.height / 2;
        });
      if (centers.length) {
        const target = centers.reduce((a, b) => a + b, 0) / centers.length;
        // The grid can be CSS-scaled on short viewports, so convert the
        // measured (scaled) delta back into layout pixels before padding.
        const scale =
          composer.offsetHeight > 0 ? cRect.height / composer.offsetHeight : 1;
        const delta = (target - (cRect.top + cRect.height / 2)) / (scale || 1);
        setComposerOffset((prev) => {
          const next = Math.min(240, Math.max(0, Math.round(prev + delta)));
          return Math.abs(next - prev) > 1 ? next : prev;
        });
      }


      // 2. Connector paths in container-local pixels.
      const cxLeft = cRect.left - base.left;
      const cxRight = cRect.right - base.left;
      const cy = cRect.top + cRect.height / 2 - base.top;

      const build = (
        refs: (HTMLDivElement | null)[],
        direction: "in" | "out",
      ) =>
        refs
          .filter(Boolean)
          .map((el) => {
            const r = (el as HTMLDivElement).getBoundingClientRect();
            const y = r.top + r.height / 2 - base.top;
            if (direction === "in") {
              const x0 = r.right - base.left;
              const mid = (x0 + cxLeft) / 2;
              return `M${x0} ${y} C ${mid} ${y}, ${mid} ${cy}, ${cxLeft} ${cy}`;
            }
            const x1 = r.left - base.left;
            const mid = (cxRight + x1) / 2;
            return `M${cxRight} ${cy} C ${mid} ${cy}, ${mid} ${y}, ${x1} ${y}`;
          });

      setOverlay({
        w: base.width,
        h: base.height,
        inputs: build(inputRefs.current, "in"),
        outputs: build(outputRefs.current, "out"),
      });
    };

    measure();
    const raf = window.requestAnimationFrame(measure);
    const ws = workspaceRef.current;
    const ro = new ResizeObserver(() => measure());
    if (ws) ro.observe(ws);
    window.addEventListener("resize", measure);
    return () => {
      window.cancelAnimationFrame(raf);
      ro.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [composerOffset, runState, visibleRuns, activeInputs, activeOutputs, scenario]);

  /**
   * Keep the proof strip under the hero on the first screen.
   *
   * The hero's height is content, not a constant: German runs the diagram's
   * card labels onto extra lines and the section goes from 948px to 1023px, so
   * a padding budget tuned on English puts the strip under the fold in every
   * other locale. Type stays at its production size; the diagram gives up the
   * difference, because a slightly smaller diagram costs less than four claims
   * nobody reads.
   *
   * Scaling alone would not help — a transform does not change layout height —
   * so the scale is paired with a negative bottom margin of exactly the height
   * it frees. Desktop only: below lg the hero is a single column with its own
   * fold rules, covered by e2e/hero-fold.spec.ts.
   */
  const [heroFit, setHeroFit] = useState({ scale: 1, pull: 0 });
  const fitRef = useRef(1);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const section = sectionRef.current;
    const ws = workspaceRef.current;
    if (!section || !ws) return;

    let queued = 0;
    let verify = 0;
    /*
     * A fit changes the section's height, which should retrigger the observer
     * below and let a second pass converge. It does not always: applying the
     * result of a ResizeObserver callback inside the observed subtree makes
     * Chrome defer the follow-up notification to the next frame, and frames do
     * not run in a background tab. So each change schedules its own check.
     * Bounded, because the passes converge on a fixed point within two or
     * three rounds.
     */
    let rounds = 0;
    const measure = () => {
      if (window.innerWidth < 1024) {
        if (fitRef.current !== 1) {
          fitRef.current = 1;
          setHeroFit({ scale: 1, pull: 0 });
        }
        return;
      }
      const strip = document.querySelector<HTMLElement>("[data-hero-strip]");
      const budget = window.innerHeight - (strip?.offsetHeight ?? 0) - HERO_FOLD_GUARD;
      const wsHeight = ws.offsetHeight;
      if (!wsHeight || budget <= 0) return;
      // section.offsetHeight already has the current pull taken out of it.
      const natural = section.offsetHeight + wsHeight * (1 - fitRef.current);
      const next =
        natural <= budget
          ? 1
          : Math.max(MIN_HERO_FIT, (wsHeight - (natural - budget)) / wsHeight);
      if (Math.abs(next - fitRef.current) < 0.005) return;
      fitRef.current = next;
      setHeroFit({ scale: next, pull: Math.round(wsHeight * (1 - next)) });
      if (rounds < 6) {
        rounds += 1;
        window.clearTimeout(verify);
        verify = window.setTimeout(measure, 150);
      }
    };

    /*
     * setTimeout rather than requestAnimationFrame. rAF does not fire in a
     * background tab, so a hero opened in a second tab kept its unfitted
     * height and only corrected itself if the visitor resized the window.
     */
    const schedule = () => {
      rounds = 0;
      window.clearTimeout(queued);
      queued = window.setTimeout(measure, 0);
    };

    schedule();
    // Web fonts and the first animation frame both change the height.
    const settle = window.setTimeout(measure, 1200);
    window.addEventListener("resize", schedule);
    /*
     * The diagram's own height is the thing that moves: a locale with longer
     * card labels wraps them onto more lines. Observing the workspace is safe
     * because neither the transform nor the negative margin changes its border
     * box, so applying a fit cannot retrigger this.
     */
    const ro = new ResizeObserver(schedule);
    ro.observe(ws);
    /*
     * The section too: the copy above and below the diagram reflows on its own
     * (text balancing, the caption). Safe against feedback because `natural`
     * adds the current pull back before comparing, so a fit that is already
     * correct recomputes to itself and returns.
     */
    ro.observe(section);
    return () => {
      window.clearTimeout(queued);
      window.clearTimeout(verify);
      window.clearTimeout(settle);
      window.removeEventListener("resize", schedule);
      ro.disconnect();
    };
  }, [i18n.language]);

  const agentBlock = (
    <StepStrip
      scenario={scenario}
      visibleRuns={visibleRuns}
      runState={runState}
    />
  );



  // Hero top padding clears the fixed nav, so it is derived, not chosen: the
  // header is 78px (24px utility tier + 1px rule + 52px nav row + 1px border)
  // and e2e/hero-fold.spec.ts requires MIN_NAV_GAP = 24px under it, so 102px
  // is the floor. pt-28 (112px) leaves 34px.
  //
  // This was 140px, tuned for the 114px header that existed before the
  // two-tier rebuild. Shrinking the header to 78px reclaimed 36px and the
  // padding kept all of it as whitespace instead of giving it to the fold,
  // which is what left no room when the subheadline grew a line.
  //
  // The shortscreen: variants below are a second budget, for laptops. The hero
  // is 800px and the proof strip under it is 53px, so the pair needs ~860px of
  // viewport. Above that the hero keeps the full spacing, which is what the
  // production page is compared against. Below it the hero gives back its
  // slack instead of pushing the strip under the fold: 102px top padding (the
  // derived floor above, not a round number) and tighter gaps around the
  // diagram. The threshold is in tailwind.config.ts, not inline, because the
  // first attempt hard-coded 860 and the same laptop reporting 861 fell back
  // to the tall layout.
  return (

    <section
      ref={sectionRef}
      id="vibe-compliance"
      className="relative overflow-hidden border-b border-border bg-background pb-6 pt-28 sm:pb-16 sm:pt-32 lg:pt-32 shortscreen:pb-6 shortscreen:pt-[102px]"
      aria-labelledby="vibe-compliance-heading"
    >

      <div className="relative mx-auto max-w-6xl px-6">
        <Reveal>
          <div className="mx-auto max-w-4xl text-center">
            <h1
              id="vibe-compliance-heading"
              className="hero-title-fluid text-balance pb-3"
            >
              <span className="sr-only">cSquare GRC: </span>
              {t("vibeHero.headline")}{" "}
              {/* Trail Teal, upright, with a marker rule sitting clear of the
                  baseline. Italic + a tight blue underline read as a hyperlink
                  inside the headline, which is the opposite of emphasis. */}
              <span className="relative inline-block text-trail-teal">
                {t("vibeHero.headlineHighlight")}
                <span
                  aria-hidden
                  className="absolute inset-x-0 -bottom-2 h-[6px] bg-trail-teal"
                />
              </span>
              .
            </h1>
            <p className="mx-auto mt-7 max-w-2xl text-pretty text-sm leading-relaxed text-muted-foreground">
              {t("vibeHero.subheadline")}
            </p>

          </div>
        </Reveal>

        {/* Workspace: inputs → composer → outputs */}
        <div
          className="relative mt-5 sm:mt-6"
          ref={workspaceRef}
          style={
            heroFit.scale < 1
              ? {
                  transform: `scale(${heroFit.scale})`,
                  transformOrigin: "top center",
                  marginBottom: `-${heroFit.pull}px`,
                }
              : undefined
          }
        >
          <FlowOverlay
            width={overlay.w}
            height={overlay.h}
            inputs={overlay.inputs}
            outputs={overlay.outputs}
            activeInputs={activeInputs}
            activeOutputs={activeOutputs}
          />

          {/* Desktop: inputs | composer | outputs */}
          <div data-testid="hero-flow" className="relative z-10 hidden origin-top lg:grid lg:grid-cols-[minmax(200px,1fr)_minmax(0,2fr)_minmax(200px,1fr)] lg:items-start lg:gap-14">
            <div className="space-y-3">
              <FlowLabel>{t("vibeHero.inputsLabel")}</FlowLabel>
              {inputCards.map((c, idx) => (
                <div
                  key={c.label}
                  ref={(el) => {
                    inputRefs.current[idx] = el;
                  }}
                >
                  <ContextCard card={c} active={idx < activeInputs && !settled} settled={settled} direction="in" />
                </div>
              ))}
            </div>

            <div className="self-start" style={{ paddingTop: composerOffset }}>
              <div ref={composerBoxRef}>
                <Composer
                  fiboStatus={runState}
                  prompt={prompt}
                  isTyping={isTyping}
                  sources={scenario.sources}
                  runState={runState}
                  longestPrompt={longestPrompt}
                />
              </div>
              <div className="mt-8">{agentBlock}</div>
            </div>

            <div className="space-y-3">
              <FlowLabel align="right">{t("vibeHero.outputsLabel")}</FlowLabel>
              {outputCards.map((c, idx) => (
                <div
                  key={c.label}
                  ref={(el) => {
                    outputRefs.current[idx] = el;
                  }}
                >
                  <ContextCard card={c} active={idx < activeOutputs && !settled} settled={settled} direction="out" />
                </div>
              ))}
            </div>

          </div>

          {/* Mobile / tablet: vertical flow with spine connectors */}
          <div className="lg:hidden">
            <div data-testid="hero-flow" className="relative origin-top shortscreen:scale-[0.9]" ref={mFlowRef}>
              <FlowOverlay
                width={mOverlay.w}
                height={mOverlay.h}
                inputs={mOverlay.inputs}
                outputs={mOverlay.outputs}
                activeInputs={activeInputs}
                activeOutputs={activeOutputs}
                variant="mobile"
              />

              <div className="relative z-10">
                <FlowLabel>{t("vibeHero.inputsLabel")}</FlowLabel>
                <div className="mt-2 sm:mt-3 grid grid-cols-3 gap-2 shortscreen:mt-1">

                  {inputCards.map((c, idx) => (
                    <div
                      key={c.label}
                      ref={(el) => {
                        mInputRefs.current[idx] = el;
                      }}
                    >
                      <FlowNode card={c} active={idx < activeInputs && !settled} settled={settled} />
                    </div>
                  ))}
                </div>

                <div className="mt-10 sm:mt-14 shortscreen:mt-5" ref={mComposerRef}>
                  <Composer
                    fiboStatus={runState}
                    prompt={prompt}
                    isTyping={isTyping}
                      sources={scenario.sources}
                    runState={runState}
                    longestPrompt={longestPrompt}
                  />
                </div>

                <div className="mt-4 sm:mt-6 shortscreen:mt-2">
                  <FlowLabel>{t("vibeHero.outputsLabel")}</FlowLabel>
                  <div className="mt-2 sm:mt-3 grid grid-cols-3 gap-2 shortscreen:mt-1">

                    {outputCards.map((c, idx) => (
                      <div
                        key={c.label}
                        ref={(el) => {
                          mOutputRefs.current[idx] = el;
                        }}
                      >
                        <FlowNode card={c} active={idx < activeOutputs && !settled} settled={settled} />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-5 sm:mt-8 shortscreen:mt-2">{agentBlock}</div>

          </div>
        </div>

        {/* Framework coverage line */}
        <p className="mx-auto mt-4 mb-3 sm:mt-8 sm:mb-4 shortscreen:-mt-8 shortscreen:mb-5 max-w-2xl px-4 text-balance text-center text-xs leading-6 text-muted-foreground sm:px-0 sm:text-sm sm:leading-7">
          {keepFrameworkNamesIntact(t("vibeHero.frameworksLine"))}
        </p>

        {/* CTAs sit BELOW the animation on purpose. e2e/hero-fold.spec.ts asserts
            the whole inputs/composer/outputs block stays above the fold at every
            viewport down to 375x667, so nothing may be added above it. It also
            matches the section's own logic: demo and coverage requests come from
            people who have already looked at the diagram. */}
        <div className="mb-2 sm:mb-4 shortscreen:mb-0 flex flex-col gap-3 sm:grid sm:w-fit sm:grid-flow-col sm:auto-cols-fr sm:mx-auto">
          <Button className="w-full" asChild size="lg">
            <LocaleLink to="/see-what-applies">
              {t("vibeHero.ctaCoverage")}
              <ArrowUp className="ml-1.5 h-4 w-4 rotate-45" />
            </LocaleLink>
          </Button>
          <Button className="w-full" asChild size="lg" variant="outline">
            <LocaleLink to="/assessment">{t("vibeHero.ctaAssessment")}</LocaleLink>
          </Button>
        </div>

      </div>
    </section>
  );
};

/** Keep multi-word framework names (SOC 2, ISO 27001, 21 CFR Part 11) on one line. */
const keepFrameworkNamesIntact = (text: string) =>
  text
    .replace(/([A-Za-z]) (\d)/g, "$1\u00A0$2")
    .replace(/(\d) ([A-Z]{2,})/g, "$1\u00A0$2")
    .replace(/(CFR\u00A0Part) (\d)/g, "$1\u00A0$2")
    .replace(/(EU) (AI) (Act)/g, "$1\u00A0$2\u00A0$3")
    .replace(/(BSA\/AML)/g, "$1");

/* ---------- subcomponents ---------- */

const Composer = ({
  fiboStatus,
  prompt,
  isTyping,
  sources,
  runState,
  longestPrompt,
}: {
  fiboStatus: RunState;
  prompt: string;
  isTyping?: boolean;
  sources: string[];
  runState: RunState;
  longestPrompt: string;
}) => {
  const { t } = useTranslation();

  return (
    <div
      data-vibe-composer
      className={cn(
        "relative border bg-card shadow-[var(--shadow-md)] transition-colors",
        runState === "running"
          ? "border-primary animate-composer-active motion-reduce:animate-none"
          : runState === "done"
          ? "border-primary/60 ring-1 ring-primary/15"
          : "border-border",
      )}
    >
      {/* Header strip */}
      <div
        className={cn(
          "flex items-center justify-between border-b border-border px-4 py-2 transition-colors",
          runState === "running" ? "bg-primary/5" : "bg-secondary/40",
        )}
      >
        <div className="flex items-center gap-3">
          <Fibo pose="point" size={36} decorative flip />
          <div className="flex items-center gap-2">
            <span
              className={cn(
                "h-2 w-2 rounded-full",
                fiboStatus === "running"
                  ? "bg-accent animate-pulse"
                  : "bg-accent",
              )}
              aria-hidden
            />
            <span className="eyebrow">
              {runState === "running"
                ? t("vibeHero.fiboStatus.running")
                : runState === "done"
                ? t("vibeHero.fiboStatus.done")
                : isTyping
                ? t("vibeHero.fiboStatus.typing")
                : t("vibeHero.fiboStatus.ready")}
            </span>
          </div>
        </div>
        <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
          {t("vibeHero.workspaceLabel")}
        </span>
      </div>

      {/* Composer body — display only, the prompt is scripted */}
      <div className="relative p-3">
        <p className="sr-only">{t("vibeHero.demoSrOnly")}</p>
        <div className="border border-border bg-background">
          <div
            aria-hidden
            className="relative px-3 py-2 text-sm leading-relaxed text-foreground"
          >
            {/* Invisible sizer: reserves height for the longest scripted prompt
                so the composer never shifts as text types in or scenarios rotate. */}
            <span className="invisible block">{longestPrompt}</span>
            <span className="absolute inset-0 px-3 py-2">
              {prompt}
              {isTyping && (
                <span className="ml-0.5 inline-block h-[1.1em] w-[2px] translate-y-[2px] animate-pulse bg-primary align-middle motion-reduce:animate-none" />
              )}
            </span>
          </div>
        </div>

      </div>

      {/* Source chips + demo CTA. Fixed-height row: the chip strip never wraps
          and the right-hand control reserves the CTA's width in both states, so
          the composer box cannot shift between run states or scenarios. */}
      <div className="flex min-h-[58px] items-center justify-between gap-3 border-t border-border px-4 py-3">
        <div className="flex min-w-0 flex-1 flex-nowrap items-center gap-1.5 overflow-hidden [mask-image:linear-gradient(to_right,black_calc(100%-24px),transparent)]">
          <span
            aria-hidden
            className="shrink-0 font-mono text-[10px] uppercase tracking-wider text-muted-foreground"
          >
            {t("vibeHero.sourcesLabel")}
          </span>
          {sources.map((s) => {
            const glyph = getBrandGlyph(s);
            return (
              <span
                key={s}
                title={s}
                className="inline-flex shrink-0 items-center border border-border bg-background px-2 py-1 text-xs text-foreground"
              >
                {glyph ? (
                  <>
                    <svg
                      viewBox="0 0 24 24"
                      className="h-3.5 w-3.5 fill-current"
                      aria-hidden
                      focusable="false"
                    >
                      <path d={glyph.path} />
                    </svg>
                    <span className="sr-only">{s}</span>
                  </>
                ) : (
                  s
                )}
              </span>
            );
          })}
        </div>

        {/* Width reserved by an invisible sizer copy of the CTA label. */}
        <div className="relative flex shrink-0 items-center justify-end">
          <span
            aria-hidden
            className="pointer-events-none invisible inline-flex items-center gap-1 px-3 text-sm"
          >
            <span className="sm:hidden">{t("vibeHero.ctaLabelShort")}</span>
            <span className="hidden sm:inline">{t("vibeHero.ctaLabel")}</span>
            <ArrowUp className="h-3 w-3" />
          </span>

          <span className="absolute inset-y-0 right-0 flex items-center">
            {runState === "running" ? (
              <span
                aria-hidden
                className="inline-flex items-center gap-1 border border-border bg-muted px-3 py-1.5 text-sm text-muted-foreground"
              >
                <Loader2 className="h-3 w-3 animate-spin motion-reduce:animate-none" />
                {t("vibeHero.running")}
              </span>
            ) : (
              <Button asChild size="sm" className="gap-1">
                <LocaleLink
                  to="/book-demo"
                  onClick={() => {
                    try {
                      (
                        window as unknown as {
                          gtag?: (...args: unknown[]) => void;
                        }
                      ).gtag?.("event", "vibe_cta_demo", { source: "composer" });
                    } catch {
                      /* noop */
                    }
                  }}
                >
                  <span className="sm:hidden">{t("vibeHero.ctaLabelShort")}</span>
                  <span className="hidden sm:inline">{t("vibeHero.ctaLabel")}</span>

                  <ArrowUp className="h-3 w-3 rotate-45" />
                </LocaleLink>
              </Button>
            )}
          </span>
        </div>
      </div>


    </div>
  );
};

/**
 * FlowOverlay — connector lines drawn from measured DOM anchors: each card
 * edge to the composer edge, in container-local pixel space. Always visible
 * at rest (border tone), escalating to primary as each card activates.
 */
const FlowOverlay = ({
  width,
  height,
  inputs,
  outputs,
  activeInputs,
  activeOutputs,
  variant = "desktop",
}: {
  width: number;
  height: number;
  inputs: string[];
  outputs: string[];
  activeInputs: number;
  activeOutputs: number;
  variant?: "desktop" | "mobile";
}) => {
  if (!width || !height) return null;

  const render = (paths: string[], activeCount: number, keyPrefix: string) =>
    paths.map((d, i) => {
      const active = i < activeCount;
      return (
        <g key={`${keyPrefix}-${i}`}>
          <path
            d={d}
            fill="none"
            stroke={active ? "hsl(var(--primary))" : "hsl(var(--border))"}
            strokeWidth={active ? 1.5 : 1}
            vectorEffect="non-scaling-stroke"
            className="transition-all duration-500"
          />
          <path
            d={d}
            fill="none"
            stroke={
              active ? "hsl(var(--primary))" : "hsl(var(--muted-foreground))"
            }
            strokeWidth={active ? 2 : 1.5}
            strokeLinecap="round"
            strokeDasharray="4 26"
            vectorEffect="non-scaling-stroke"
            className="flow-dash"
            style={{ animationDelay: `${i * 0.45}s` }}
            opacity={active ? 0.9 : 0.5}
          />
        </g>
      );
    });

  return (
    <svg
      aria-hidden
      className={cn(
        "pointer-events-none absolute inset-0 z-0 h-full w-full",
        variant === "mobile" ? "block lg:hidden" : "hidden lg:block",
      )}
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
    >
      {render(inputs, activeInputs, "in")}
      {render(outputs, activeOutputs, "out")}
    </svg>
  );
};

/**
 * FlowNode — compact icon-first node used in the mobile vertical flow.
 * Short label only; the full label is exposed to assistive tech.
 */
const FlowNode = ({
  card,
  active,
  settled = false,
}: {
  card: FlowCard;
  active: boolean;
  /** The run has finished: quiet border, but full opacity. */
  settled?: boolean;
}) => {
  const Icon = card.icon;
  return (
    <div
      title={card.label}
      aria-label={`${card.label} — ${card.sub}`}
      className={cn(
        "flex min-h-[64px] flex-col items-center justify-center gap-1.5 border bg-card px-1.5 py-3 text-center transition-all duration-500",
        active
          ? "border-primary bg-primary/[0.04]"
          : settled
          ? "border-border opacity-100"
          : "border-border opacity-80",
      )}
    >
      <Icon
        className={cn(
          "h-5 w-5 transition-colors",
          active ? "text-primary" : "text-muted-foreground",
        )}
        aria-hidden
      />
      <span className="font-mono text-[10px] uppercase leading-tight tracking-wider text-foreground">
        {card.short ?? card.label}
      </span>
    </div>
  );
};





const FlowLabel = ({
  children,
  align = "left",
}: {
  children: ReactNode;
  align?: "left" | "right";
}) => (
  <p
    className={cn(
      "font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground",
      align === "right" && "lg:text-right",
    )}
  >
    {children}
  </p>
);

type FlowCard = {
  icon: (typeof INPUT_CARD_DEFS)[number]["icon"];
  label: string;
  short?: string;
  sub: string;
  chips: string[];
  animated?: boolean;
};

const ContextCard = ({
  card,
  className,
  compact = false,
  active = false,
  settled = false,
  direction = "in",
}: {
  card: FlowCard;
  className?: string;
  compact?: boolean;
  active?: boolean;
  /** The run has finished: quiet border, but full opacity. */
  settled?: boolean;
  direction?: "in" | "out";
}) => {
  const Icon = card.icon;
  return (
    <div
      className={cn(
        "border bg-card p-3 transition-all duration-500",
        active
          ? "border-primary bg-primary/[0.04] translate-x-0 opacity-100"
          : settled
          ? "border-border translate-x-0 opacity-100"
          : direction === "out"
          ? "border-border opacity-70"
          : "border-border opacity-100",
        className,
      )}
    >
      <div className="flex items-center gap-2">
        <Icon
          className={cn(
            "h-4 w-4 transition-colors",
            active ? "text-primary" : "text-muted-foreground",
          )}
          aria-hidden
        />
        <span className="font-mono text-xs uppercase tracking-wider text-foreground">
          {card.label}
        </span>
      </div>
      <p className="mt-0.5 text-[11px] text-muted-foreground">{card.sub}</p>
      {card.animated && !compact && <WorkflowChain active={active} />}
      {!compact && (
        <div className="mt-2 flex flex-wrap gap-1">
          {card.chips.slice(0, 6).map((c, i) => {
            const glyph = getBrandGlyph(c);
            const hasFrameworkLogo = !glyph && Boolean(getFrameworkLogoDomain(c));
            return (
              <span
                key={c}
                title={c}
                className={cn(
                  "inline-flex items-center border px-1.5 py-0.5 text-[10px] transition-colors duration-300",
                  glyph && "py-1",
                  hasFrameworkLogo && "gap-1",
                  active
                    ? "border-primary/50 bg-primary/10 text-foreground"
                    : "border-border bg-background text-muted-foreground",
                )}
                style={active ? { transitionDelay: `${i * 90}ms` } : undefined}
              >
                {glyph ? (
                  <>
                    <svg
                      viewBox="0 0 24 24"
                      className="h-3 w-3 fill-current"
                      aria-hidden
                      focusable="false"
                    >
                      <path d={glyph.path} />
                    </svg>
                    <span className="sr-only">{c}</span>
                  </>
                ) : (
                  <>
                    <FrameworkLogo name={c} size={12} />
                    {c}
                  </>
                )}
              </span>
            );
          })}

        </div>
      )}
    </div>
  );
};

const WORKFLOW_NODE_KEYS = ["collect", "test", "review", "approve"] as const;

const WorkflowChain = ({ active }: { active: boolean }) => {
  const { t } = useTranslation();
  const WORKFLOW_NODES = WORKFLOW_NODE_KEYS.map((k) =>
    t(`vibeHero.workflowNodes.${k}`),
  );
  return (
  <div className="mt-3" aria-hidden>
    <svg viewBox="0 0 120 24" className="h-6 w-full">
      {/* Rail */}
      <line
        x1="8"
        y1="12"
        x2="112"
        y2="12"
        stroke="hsl(var(--border))"
        strokeWidth="2"
      />
      {/* Travelling flow — visible at rest, primary tone during a run */}
      <line
        x1="8"
        y1="12"
        x2="112"
        y2="12"
        stroke={
          active ? "hsl(var(--primary))" : "hsl(var(--muted-foreground))"
        }
        strokeWidth={active ? 2.5 : 2}
        strokeLinecap="round"
        strokeDasharray="4 18"
        opacity={active ? 0.9 : 0.45}
        className="flow-dash"
      />
      {WORKFLOW_NODES.map((n, i) => (
        <circle
          key={n}
          cx={8 + i * 34.6}
          cy={12}
          r={active ? 5 : 3.5}
          fill={active ? "hsl(var(--primary))" : "hsl(var(--muted-foreground))"}
          opacity={active ? 1 : 0.55}
          className="transition-all duration-500"
        >
          {active && (
            <animate
              attributeName="r"
              values="4;6.5;4"
              dur="1.6s"
              begin={`${i * 0.4}s`}
              repeatCount="indefinite"
            />
          )}
        </circle>
      ))}
    </svg>
    <div className="mt-1.5 flex justify-between font-mono text-[9px] uppercase tracking-wider text-muted-foreground">
      {WORKFLOW_NODES.map((n) => (
        <span key={n}>{n}</span>
      ))}
    </div>
  </div>
  );
};



/**
 * StepStrip — single-line run summary. Only the current step is shown, on one
 * row, so the hero animation stays compact and never reflows mid-run.
 */
const StepStrip = ({
  scenario,
  visibleRuns,
  runState,
}: {
  scenario: VibeScenario;
  visibleRuns: number;
  runState: RunState;
}) => {
  const steps = scenario.steps.slice(0, 3);
  const index = Math.min(Math.max(visibleRuns - 1, 0), steps.length - 1);
  const current = visibleRuns > 0 ? steps[index] : "";
  const isRunning = runState === "running";

  return (
    <div
      aria-hidden
      className={cn(
        // Height stays reserved so the hero never reflows, but the frame only
        // appears once there is something to frame.
        "flex h-12 items-center gap-3 border px-4 transition-colors duration-300",
        visibleRuns > 0
          ? "border-border bg-card"
          : "border-transparent bg-transparent",
      )}
    >
      {visibleRuns > 0 && (
        <>
          {isRunning ? (
            <Loader2 className="h-4 w-4 shrink-0 animate-spin text-primary motion-reduce:animate-none" />
          ) : (
            <CheckCircle2 className="h-4 w-4 shrink-0 text-accent" />
          )}
          <span
            key={`${scenario.id}-${index}`}
            className="min-w-0 flex-1 truncate text-sm text-foreground"
          >
            {current}
          </span>
          <span className="shrink-0 font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
            {Math.min(visibleRuns, steps.length)}/{steps.length}
          </span>
        </>
      )}
    </div>
  );
};



export default VibeCompliance;
